<template lang="pug">
  div.snapshot-wrapper(
    :style="{ top: snapshotTopPosition + 'px' }"
    ref="snapshotWrapper"
  )
    .loading-dots(v-if="loading")
      .dot1
      .dot2
      .dot3
    transition(name="snapshot-anim" v-else-if="patient")
      .snapshot
        h3
          router-link.link-highlight(:to="{ name: 'Patient', params: { id: patient.id }, query: { coaching: true, supervisor: isSupervisorReview }}")
            | {{ patient.firstName }} {{ patient.lastName }}
        p {{ patient.emailAddress }}
        p.error(v-if="!patient.profileComplete") Profile Incomplete
        p
          strong Coaching status:&nbsp;
          | {{ patientCoachingData.coachingStatus | snakeToCamelCase }}
        template(v-if="isSupervisorReview && isCoachSupervisor")
          p
            strong Last supervisor review:&nbsp;
            template(v-if="supervisorReviewDate === null")
              | Not reviewed
            template(v-else)
              | {{ supervisorReviewDate | longDate }}
          ul.actions.supervisor-actions(v-if="isSupervisorReview")
            li
              a.btn-supervisor-review.btn.success(
                @click="doSetPatientSupervisorReview(patient.id)"
                title="View member profile"
                tag="button"
                :class="{ 'focus': supervisorReviewBtn.disabled }"
                :disabled="supervisorReviewBtn.disabled")
                | Mark patient as reviewed
        coaching-recent-chat(
          :patientId="patient.id"
          :lastSupervisorReview="supervisorReviewDate")
        assessments(:scores="patient.assessmentScores" :noDataMsg="noDataMsg")
        tracker-chart(:tracker="defaultTracker" :noDataMsg="noDataMsg")
        dl
          dt Recent User Goal
          dd(v-if="!recentGoal")
            | {{ noDataMsg }}
          dd(v-if="recentGoal")
            p &ldquo;{{ recentGoal }}&rdquo;
        interaction-history(:loginHistory="patient.loginHistory" :showRecentOnly="true" :patientAge="patient.age" :patientGender="patient.gender")
        ul.actions
          li
            router-link.btn(
              title="View member profile"
              :to="{ name: 'Patient', params: { id: patient.id }, query: { coaching: true, supervisor: isSupervisorReview }}")
              | View Member Detail
    .empty-snapshot(v-else)
      p Click on a user row to view a snapshot of their recent data.
      img(src="/static/images/empty-snapshot.svg")
</template>

<script>
import snakeToCamelCase from '@/filters/snakeToCamelCase.js';
import CoachingRecentChat from '@/components/coaching/CoachingRecentChat.vue';
import Assessments from '@/components/patient/snapshot/Assessments';
import TrackerChart from '@/components/patient/snapshot/TrackerChart';
import InteractionHistory from '@/components/patient/common/InteractionHistory';

import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'CoachingSnapshot',
  components: {
    CoachingRecentChat,
    Assessments,
    TrackerChart,
    InteractionHistory
  },
  data() {
    return {
      noDataMsg: 'No data captured',
      snapshotTopPosition: 0,
      supervisorReviewDate: null,
      supervisorReviewBtn: {
        disabled: false
      }
    };
  },
  props: {
    patient: {
      required: false
    },
    patientCoachingData: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean
    },
    markReviewed: {
      type: Function,
      required: true
    },
    isSupervisorReview: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  filters: {
    snakeToCamelCase
  },
  computed: {
    recentGoal: function() {
      return _.get(this.patient, 'goals[0]');
    },
    defaultTracker: function() {
      // return emotional health data
      if (this.patient && this.patient.trackerHistory) {
        return _.find(this.patient.trackerHistory, 'isRequired');
      }
      return {};
    },
    ...mapGetters([
      'isCoachSupervisor'
    ])
  },
  methods: {
    init() {
      this.supervisorReviewBtn.disabled = false;
      this.setSupervisorReviewDate();
    },
    setSnapshotPosition() {
      // Get html scroll position and snapshot offset
      var htmlScrollPosition = parseInt(window.pageYOffset);
      var snapshotOffset = this.$refs.snapshotWrapper.parentElement.offsetTop;
      var newSnapshotTopPosition = htmlScrollPosition - snapshotOffset;

      // Set snapshot top position
      this.snapshotTopPosition = newSnapshotTopPosition >= 0 ? newSnapshotTopPosition : 0;
    },
    doSetPatientSupervisorReview(patientId) {
      if (this.supervisorReviewBtn.disabled) {
        return;
      }

      this.supervisorReviewBtn.disabled = true;
      this.setPatientSupervisorReview(patientId).then(
        () => {
          // Update supervisorReviewDate
          this.supervisorReviewDate = new Date().getTime();
        },
        () => {
          this.supervisorReviewBtn.disabled = false;
        });
    },
    setSupervisorReviewDate() {
      this.supervisorReviewDate = this.patientCoachingData.supervisorReviewDate;
    },
    ...mapActions([
      'setPatientSupervisorReview'
    ])
  },
  watch: {
    loading() {
      this.setSnapshotPosition();
    },
    patient() {
      this.init();
      this.setSnapshotPosition();
    },
    patientCoachingData: {
      handler() {
        this.setSupervisorReviewDate();
      },
      deep: true
    }
  },
  updated() {
    this.setSupervisorReviewDate();
  },
  mounted() {
    this.setSupervisorReviewDate();
  }
};
</script>

<style lang="scss">
  @keyframes fade-in {
    0% { opacity: 0 }
    100% { opacity: 1 }
  }
  .snapshot-wrapper {
    position: relative;
    margin-bottom: 2em;
  }
  .empty-snapshot {
    text-align: center;
    color: $dark-color;
    animation: fade-in 2.5s ease-in;
    p {
      position: relative;
      font-family: $serif-font-family;
      font-size: $base-font-size;
      line-height: 1.25;
      @include zero;
      padding: $small-spacing;
      background: $base-background-color;
      border: 1px solid #fff;
      @include drop-shadow;
      border-bottom: 1px solid $dark-color;
      &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 50%;
        @include size(10px);
        margin: 0 0 -5px -5px;
        background: $dark-color;
        border-radius: 5px;
      }
    }
    img {
      @include zero;
      display: block;
      width: 50%;
      margin-right: auto;
      position: relative;
      margin-top: -6px;
      z-index: -1;
    }
  }
  .snapshot {
    background: #fff;
    padding: $small-spacing;
    @include drop-shadow;
    .error {
      font-size: $ms-down1;
      color: $error-color;
    }
    a {
      &.btn-supervisor-review.focus {
        opacity: 0.8;
      }
    }
    p {
      font-size: $ms-down1;
      line-height: 1.2;
      strong {
        font-weight: $base-font-weight-strong;
      }
    }
    dl {
      @include zero;
      margin-bottom: $small-spacing;
      &:last-of-type {
        margin-bottom: 0;
      }
    }
    dt {
      font-size: $ms-down1;
      font-weight: $base-font-weight-strong;
      text-transform: uppercase;
      border-top: 1px solid $black;
      margin: 0 0 .25em 0;
    }
    dd {
      font-size: $ms-down1;
      line-height: 1.2;
      margin-bottom: .25em;
    }
    table {
      margin: 0 auto;
      border-top: 1px solid $base-border-color;
    }
    tr:last-of-type {
      td { border: none; }
    }
    ul.actions {
      margin: $base-spacing auto;

      &.supervisor-actions {
        margin: $small-spacing auto;
      }
      li {
        display: block;
        font-size: $ms-down1;
        margin-bottom: .5em;
      }
      .btn {
        width: 100%;
        text-align: center;
        font-family: $base-font-family;
        font-weight: $base-font-weight-strong;
      }
    }

  }


  // TRANSITIONS
  .snapshot-anim-enter-active {
    transition: all .3s ease;
  }
  .snapshot-anim-leave-active {
    transition: all .25s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }
  .snapshot-anim-enter, .snapshot-anim-leave-to {
    transform: translateX(100%);
    opacity: 0;
  }

</style>
